import * as React from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { CheckIcon, UsersIcon } from 'lucide-react'

import { getFilterOptions, type TeamFilterOption } from '@/api/teamInsights'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

interface TeamFilterProps {
  value: string | null
  onChange: (next: string | null) => void
  disabled?: boolean
  // Applied to the trigger Button — lets parents stretch the pill to fill a grid cell.
  triggerClassName?: string
}

const TeamFilter = ({ value, onChange, disabled = false, triggerClassName }: TeamFilterProps) => {
  const enterpriseId = useSelector(selectCurrentEnterprise)?.id ?? null
  const [open, setOpen] = React.useState(false)

  const filterOptions = useQuery({
    queryKey: ['team-insights', 'filter-options', enterpriseId],
    queryFn: () => getFilterOptions(enterpriseId!).then((r) => r.data),
    enabled: (open || !!value) && !!enterpriseId,
    staleTime: 5 * 60 * 1000,
  })

  const teams: TeamFilterOption[] = filterOptions.data?.teams ?? []
  const selectedTeam = teams.find((t) => t.id === value)
  const triggerLabel = value ? (selectedTeam?.name ?? `Team: ${value}`) : 'All teams'

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setOpen(false)
          return
        }
        if (!disabled) setOpen(true)
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="lg" disabled={disabled} className={cn(triggerClassName)}>
          <UsersIcon className="size-[22px]" />
          <span className={cn('truncate text-left', triggerClassName ? 'flex-1' : 'max-w-[160px]')}>
            {triggerLabel}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandList>
            {filterOptions.isLoading && <div className="text-muted-foreground py-6 text-center text-sm">Loading…</div>}
            {filterOptions.isError && (
              <div className="text-muted-foreground py-6 text-center text-sm">Couldn’t load teams.</div>
            )}
            {!filterOptions.isLoading && !filterOptions.isError && (
              <>
                <CommandEmpty>No teams found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="__all__"
                    onSelect={() => {
                      onChange(null)
                      setOpen(false)
                    }}
                  >
                    <CheckIcon className={cn('size-4', value === null ? 'opacity-100' : 'opacity-0')} />
                    All teams
                  </CommandItem>
                  {teams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={team.name}
                      onSelect={() => {
                        onChange(team.id === value ? null : team.id)
                        setOpen(false)
                      }}
                    >
                      <CheckIcon className={cn('size-4', value === team.id ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{team.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default TeamFilter
